import { PASSWORD_RULES } from './passwordPolicy';
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { API, ROLE_LABEL, Role, useAuth } from './auth';

/** VERAX: Usuarios y auditoría (rol ADMIN o superior). */

interface U { id: string; email: string; name: string; role: Role; company: { id: string; name: string } | null; active: boolean; mfaEnabled: boolean; lastLoginAt: string | null }
interface A { id: string; at: string; actorEmail: string | null; actorRole: string | null; action: string; result: string; resource: string | null }

const ROLES: Role[] = ['SUPER_ADMIN', 'ADMIN', 'ANALYST', 'OPERATOR', 'VIEWER'];

export const UsersPanel: React.FC<{ companies: { id: string; name: string }[] }> = ({ companies }) => {
  const { user } = useAuth();
  const [users, setUsers] = useState<U[]>([]);
  const [audit, setAudit] = useState<A[]>([]);
  const [form, setForm] = useState({ email: '', name: '', role: 'ANALYST' as Role, companyId: '', password: '' });
  const [msg, setMsg] = useState('');
  const [reset, setReset] = useState<{ id: string; password: string } | null>(null);

  const load = useCallback(async () => {
    const [u, a] = await Promise.all([axios.get(`${API}/users`), axios.get(`${API}/audit`)]);
    setUsers(u.data); setAudit(a.data);
  }, []);
  useEffect(() => { load().catch(() => setMsg('No se pudo cargar la lista de usuarios')); }, [load]);

  const crear = async (e: React.FormEvent) => {
    e.preventDefault(); setMsg('');
    try {
      await axios.post(`${API}/users`, { ...form, companyId: form.companyId || null });
      setForm({ email: '', name: '', role: 'ANALYST', companyId: '', password: '' });
      setMsg('Usuario creado. Deberá activar su segundo factor en el primer ingreso.');
      load();
    } catch (err: any) { setMsg(err?.response?.data?.error || 'No se pudo crear'); }
  };
  const act = async (fn: () => Promise<any>, ok: string) => {
    setMsg('');
    try { await fn(); setMsg(ok); load(); } catch (err: any) { setMsg(err?.response?.data?.error || 'Operación rechazada'); }
  };

  const disponibles = ROLES.filter(r => user?.role === 'SUPER_ADMIN' || ROLES.indexOf(r) > ROLES.indexOf(user!.role));

  return (
    <section className="mb-10">
      <h2 className="text-2xl font-bold mb-4">Usuarios y accesos</h2>
      {msg && <p className="text-sm text-cyan-300 mb-3" role="status">{msg}</p>}
      <form onSubmit={crear} className="bg-gray-900 border border-gray-800 rounded-lg p-4 grid md:grid-cols-6 gap-2 mb-4 items-end">
        <input required placeholder="Nombre" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="bg-gray-950 border border-gray-700 rounded px-2 py-2 text-sm" />
        <input required type="email" placeholder="Correo" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className="bg-gray-950 border border-gray-700 rounded px-2 py-2 text-sm" />
        <select value={form.role} onChange={e => setForm({ ...form, role: e.target.value as Role })} className="bg-gray-950 border border-gray-700 rounded px-2 py-2 text-sm">
          {disponibles.map(r => <option key={r} value={r}>{ROLE_LABEL[r]}</option>)}
        </select>
        {user?.role === 'SUPER_ADMIN' ? (
          <select value={form.companyId} onChange={e => setForm({ ...form, companyId: e.target.value })} className="bg-gray-950 border border-gray-700 rounded px-2 py-2 text-sm">
            <option value="">Equipo operador (todas)</option>
            {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        ) : <span className="text-xs text-gray-500">Tu empresa</span>}
        <div>
          <input required type="password" minLength={12} placeholder="Contraseña inicial" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} className="w-full bg-gray-800 border border-gray-700 rounded px-2 py-2 text-sm" />
          {form.password && (
            <ul className="mt-1 text-[11px] space-y-0.5">
              {PASSWORD_RULES.map(r => (
                <li key={r.id} className={r.test(form.password) ? 'text-emerald-400' : 'text-gray-500'}>{r.test(form.password) ? '✓' : '·'} {r.label}</li>
              ))}
            </ul>
          )}
        </div>
        <button className="bg-cyan-700 hover:bg-cyan-600 rounded px-3 py-2 text-sm font-semibold">Crear usuario</button>
      </form>
      <div className="overflow-x-auto bg-gray-900 border border-gray-800 rounded-lg mb-6">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-[11px] uppercase tracking-wider text-gray-500 border-b border-gray-800">
            <th className="p-3">Usuario</th><th>Rol</th><th>Alcance</th><th>MFA</th><th>Último ingreso</th><th></th></tr></thead>
          <tbody>
            {users.map(u => (
              <tr key={u.id} className="border-b border-gray-800/60">
                <td className="p-3"><p>{u.name}</p><p className="text-xs text-gray-500">{u.email}</p></td>
                <td>{ROLE_LABEL[u.role]}</td>
                <td className="text-xs text-gray-400">{u.company?.name ?? 'Operador (todas)'}</td>
                <td>{u.mfaEnabled ? <span className="text-emerald-400 text-xs">✓ Activo</span> : <span className="text-amber-400 text-xs">◷ Pendiente</span>}</td>
                <td className="text-xs text-gray-400">{u.lastLoginAt ? new Date(u.lastLoginAt).toLocaleString('es-CO') : '-'}</td>
                <td className="text-right pr-3 space-x-2 whitespace-nowrap">
                  {u.id !== user?.id && <>
                    <button onClick={() => act(() => axios.put(`${API}/users/${u.id}`, { active: !u.active }), u.active ? 'Usuario desactivado' : 'Usuario activado')}
                      className="text-xs text-gray-300 hover:text-white">{u.active ? 'Desactivar' : 'Activar'}</button>
                    <button onClick={() => act(() => axios.post(`${API}/users/${u.id}/reset-mfa`), 'MFA reiniciado: deberá enrolarlo de nuevo')}
                      className="text-xs text-gray-300 hover:text-white">Reiniciar MFA</button>
                    <button onClick={() => { setReset(reset?.id === u.id ? null : { id: u.id, password: '' }); }}
                      className="text-xs text-gray-300 hover:text-white">Restablecer contraseña</button>
                  </>}
                  {reset?.id === u.id && (
                    <div className="mt-2 text-left inline-block w-72">
                      <input type="password" autoComplete="new-password" placeholder="Nueva contraseña" value={reset.password}
                        onChange={e => setReset({ id: u.id, password: e.target.value })}
                        className="w-full bg-gray-800 border border-gray-700 rounded px-2 py-1.5 text-sm" />
                      <ul className="mt-1 text-[11px] space-y-0.5">
                        {PASSWORD_RULES.map(r => (
                          <li key={r.id} className={r.test(reset.password) ? 'text-emerald-400' : 'text-gray-500'}>{r.test(reset.password) ? '✓' : '·'} {r.label}</li>
                        ))}
                      </ul>
                      <button disabled={!PASSWORD_RULES.every(r => r.test(reset.password))}
                        onClick={() => act(() => axios.post(`${API}/users/${u.id}/reset-password`, { password: reset.password }), 'Contraseña restablecida: la cuenta quedó desbloqueada').then(() => setReset(null))}
                        className="mt-1 text-xs bg-blue-700 hover:bg-blue-600 disabled:opacity-40 px-3 py-1 rounded">Guardar</button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <h3 className="text-lg font-semibold mb-2">Registro de auditoría <span className="text-xs text-gray-500 font-normal">(últimos 200 eventos · solo inserción)</span></h3>
      <div className="overflow-auto max-h-80 bg-gray-900 border border-gray-800 rounded-lg">
        <table className="w-full text-xs">
          <tbody>
            {audit.map(a => (
              <tr key={a.id} className="border-b border-gray-800/60">
                <td className="p-2 text-gray-500 whitespace-nowrap">{new Date(a.at).toLocaleString('es-CO')}</td>
                <td className="text-gray-300">{a.actorEmail ?? '-'}</td>
                <td className="text-gray-500">{a.actorRole ?? ''}</td>
                <td className="font-mono text-gray-300">{a.action}</td>
                <td className={/^(2|OK)/.test(a.result) ? 'text-emerald-400' : 'text-amber-400'}>{a.result}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
};
